/**
 * Univers — Beach Club (iter-51 · date-first refonte).
 *
 * The user lands on a clean visual page (image + title + subtitle + calendar).
 * No offers are shown until a date is picked. Once a date is selected, the
 * available offer(s) for that weekday are revealed, alongside any published
 * special events scheduled on the same day.
 *
 *   Monday — Friday → Day Pass
 *   Saturday        → Sunset Experience
 *   Sunday          → B Brunch
 *
 * Special events come live from /api/special-events?date=YYYY-MM-DD.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { fr as frLocale } from "date-fns/locale";
import { ArrowRight, Sparkles } from "lucide-react";
import { Calendar } from "../../components/ui/calendar";
import api from "../../lib/api";
import { trackEvent } from "../../lib/tracking";
import { useSiteConfig, sel } from "../../lib/site-config";
import HeroBackground from "../../components/vitrine/HeroBackground";

const HERO_IMAGE_FALLBACK =
  "https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/trz2j0jd_BEACH%20CLUB.png";

// Weekday → offer mapping (date-fns getDay: 0=Sun, 1=Mon, …, 6=Sat).
// iter-112 · Prices are NEVER hardcoded here — pulled at runtime from
// /api/offers/{id} so a Dashboard change is reflected on the front instantly.
const OFFER_BY_WEEKDAY = {
  0: {  // Sunday
    id: "brunch",
    title: "B Brunch",
    description: "Le brunch attendu de tout Abidjan. Vue panoramique et buffet d'exception.",
    image: "https://customer-assets-jt897jd0.emergentagent.net/job_reserve-bbr/artifacts/c04z1595_Image%20site%20beach%20club_Plan%20de%20travail%201.webp",
    to: "/booking/brunch",
  },
  6: {  // Saturday
    id: "sunset",
    title: "Sunset Experience",
    description: "Le coucher de soleil le plus spectaculaire d'Abidjan, en cocktail.",
    image: "https://customer-assets-jt897jd0.emergentagent.net/job_reserve-bbr/artifacts/r3dvpdje_Image%20site%20beach%20club-03.webp",
    to: "/booking/sunset",
  },
};
// iter-96 · Sunset Club — additional Saturday experience (15h—22h, gender-based pricing).
const SATURDAY_SUNSET_CLUB = {
  id: "sunset_club",
  title: "Sunset Club",
  description: "Every Saturday · 15:00 – 22:00. DJ set · champagne · vue lagune. Ladies gratuit · Gentlemen 25 000 FCFA.",
  image: "https://customer-assets-jt897jd0.emergentagent.net/job_reserve-bbr/artifacts/r3dvpdje_Image%20site%20beach%20club-03.webp",
  to: "/booking/sunset_club",
};
const WEEKDAY_DAY_PASS = {
  id: "pass_day",
  title: "Day Pass",
  description: "L'accès complet au beach club pour une journée d'évasion totale.",
  image: "https://customer-assets-jt897jd0.emergentagent.net/job_reserve-bbr/artifacts/z00iovry_Image%20site%20beach%20club-02.webp",
  to: "/booking/pass_day",
};

function offersForDate(d) {
  if (!d) return [];
  const wd = d.getDay();
  if (wd === 0 || wd === 6) return [OFFER_BY_WEEKDAY[wd]];  // Sunday → brunch, Saturday → sunset
  return [WEEKDAY_DAY_PASS]; // Mon–Fri
}

export default function UniversBeachClub() {
  const [selected, setSelected] = useState(null);
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [offerPrices, setOfferPrices] = useState({});  // iter-112 · id → merged offer
  const offersRef = useRef(null);

  // iter-112 · Fetch live offer prices from the Dashboard-driven API so the
  // Beach Club cards ALWAYS display what the CMS has configured (no static
  // price string can ever drift out of sync).
  useEffect(() => {
    api.get("/offers").then((r) => {
      const list = r.data || [];
      const map = {};
      for (const o of list) map[o.id] = o;
      setOfferPrices(map);
    }).catch(() => setOfferPrices({}));
  }, []);

  const iso = useMemo(() => (selected ? format(selected, "yyyy-MM-dd") : null), [selected]);
  const weekdayLabel = useMemo(
    () => (selected ? format(selected, "EEEE d MMMM yyyy", { locale: frLocale }) : ""),
    [selected]
  );
  const offers = useMemo(() => offersForDate(selected), [selected]);

  // iter-63 — CMS-driven hero content
  const cfg = useSiteConfig();
  const ph = sel.pageHeader(cfg, "hero_beach_club");
  const heroImage = ph.image_url || HERO_IMAGE_FALLBACK;
  const heroKicker = ph.kicker || "· Beach Club ·";
  const heroTitle = ph.title || "Choisissez votre journée";
  const heroSubtitle = ph.subtitle_long || "Sélectionnez la date de votre visite et découvrez les expériences disponibles pour cette journée. Chaque date révèle une sélection d'offres uniques, pensées pour vous faire vivre pleinement l'univers BBr.";
  const heroFooter = ph.footer_note || "Day Pass · Sunset Experience · B Brunch";
  const heroVideo = ph.video_url || "";

  useEffect(() => {
    if (!iso) { setEvents([]); return; }
    setLoadingEvents(true);
    api
      .get(`/special-events?date=${iso}`)
      .then((r) => setEvents(r.data?.items || []))
      .catch(() => setEvents([]))
      .finally(() => setLoadingEvents(false));
  }, [iso]);

  // Smooth scroll to offers section as soon as a date is picked.
  useEffect(() => {
    if (!selected || !offersRef.current) return;
    const t = setTimeout(() => {
      offersRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
    return () => clearTimeout(t);
  }, [selected]);

  return (
    <div className="bg-white text-[#0A0A0A]" data-testid="univers-beach-club">
      {/* ─── Immersive Hero — image + integrated calendar card ─── */}
      <section className="relative w-full min-h-[100vh] md:min-h-[100vh] overflow-hidden bg-[#0A0A0A]">
        <HeroBackground videoUrl={heroVideo} imageUrl={heroImage} />
        {/* Darker overlay for premium legibility of white header + centered title + calendar card */}
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/40" />

        <div className="relative z-10 min-h-[100vh] max-w-[1500px] mx-auto px-6 md:px-12 lg:px-20 py-24 md:py-28 flex flex-col items-center justify-center gap-8 md:gap-10">
          {/* Compact title block — kept for mobile stacking; hidden on desktop where it moves into the right column of the horizontal card */}
          <div className="md:hidden text-center text-white">
            <div className="text-[0.6rem] tracking-[0.55em] uppercase text-white/85 mb-4">
              {heroKicker}
            </div>
            <h1 className="font-serif font-light text-4xl sm:text-5xl leading-[1.05] whitespace-pre-line">
              {heroTitle}
            </h1>
          </div>

          {/* MOBILE: single-column calendar card (unchanged) */}
          <div
            className="md:hidden w-full max-w-md bg-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)] p-4 sm:p-6"
            data-testid="bc-calendar-card-mobile"
          >
            <Calendar
              mode="single"
              selected={selected}
              onSelect={(d) => {
                setSelected(d);
                if (d) trackEvent("bc_date_select", { date: format(d, "yyyy-MM-dd") });
              }}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              locale={frLocale}
              className="mx-auto"
              classNames={{
                day_today: "bg-transparent text-[#0A0A0A] hover:bg-[#B8922A]/10",
                day_selected:
                  "bg-[#B8922A] text-white hover:bg-[#B8922A] hover:text-white focus:bg-[#B8922A] focus:text-white",
              }}
              data-testid="bc-calendar-mobile"
            />
            {selected && (
              <div className="text-center mt-4 pt-4 border-t border-[#0A0A0A]/10 text-xs tracking-[0.15em] uppercase text-[#0A0A0A]/70">
                <span className="capitalize">{weekdayLabel}</span>
              </div>
            )}
          </div>

          {/* DESKTOP: horizontal landscape card — calendar (left) + context (right) */}
          <div
            className="hidden md:grid w-full max-w-5xl grid-cols-2 bg-white shadow-[0_40px_100px_-30px_rgba(0,0,0,0.6)]"
            data-testid="bc-calendar-card-desktop"
          >
            {/* LEFT — Calendar */}
            <div className="p-8 lg:p-10 border-r border-[#0A0A0A]/8">
              <Calendar
                mode="single"
                selected={selected}
                onSelect={(d) => {
                  setSelected(d);
                  if (d) trackEvent("bc_date_select", { date: format(d, "yyyy-MM-dd") });
                }}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                locale={frLocale}
                className="mx-auto scale-[1.08] origin-top"
                classNames={{
                  day_today: "bg-transparent text-[#0A0A0A] hover:bg-[#B8922A]/10",
                  day_selected:
                    "bg-[#B8922A] text-white hover:bg-[#B8922A] hover:text-white focus:bg-[#B8922A] focus:text-white",
                }}
                data-testid="bc-calendar"
              />
              {selected && (
                <div className="text-center mt-6 pt-4 border-t border-[#0A0A0A]/10 text-xs tracking-[0.2em] uppercase text-[#0A0A0A]/70">
                  <span className="capitalize">{weekdayLabel}</span>
                </div>
              )}
            </div>

            {/* RIGHT — Context block */}
            <div className="p-10 lg:p-14 flex flex-col justify-center text-[#0A0A0A]">
              <div className="text-[0.62rem] tracking-[0.55em] uppercase text-[#B8922A] mb-6">
                {heroKicker}
              </div>
              <h1 className="font-serif font-light text-4xl lg:text-5xl leading-[1.05] mb-8 whitespace-pre-line">
                {heroTitle}
              </h1>
              <p className="text-sm lg:text-base leading-[1.85] text-[#0A0A0A]/70 font-light">
                {heroSubtitle}
              </p>
              {heroFooter && (
                <div className="mt-8 pt-6 border-t border-[#0A0A0A]/10 text-[0.6rem] tracking-[0.4em] uppercase text-[#0A0A0A]/45">
                  {heroFooter}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Offers + special events — revealed only after a date is picked */}
      {selected && (
        <section
          ref={offersRef}
          className="py-20 md:py-28 bg-white scroll-mt-24"
          data-testid="bc-offers-section"
        >
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <div className="text-[0.6rem] tracking-[0.45em] uppercase text-[#B8922A] mb-4">
                · Étape 2 ·
              </div>
              <h2 className="font-serif font-light text-3xl md:text-4xl leading-tight">
                Disponibles ce jour
              </h2>
            </div>
            <div
              className={`grid gap-6 md:gap-8 ${
                offers.length + events.length > 1 ? "md:grid-cols-2" : "md:grid-cols-1 max-w-xl mx-auto"
              }`}
            >
              {offers.map((o) => (
                <OfferCard key={o.id} o={o} live={offerPrices[o.id]} dateLabel={weekdayLabel} iso={iso} />
              ))}
              {events.map((ev) => (
                <EventCard key={ev.id} ev={ev} iso={iso} />
              ))}
              {loadingEvents && (
                <div className="md:col-span-2 text-center text-sm text-[#0A0A0A]/45">
                  Chargement des événements…
                </div>
              )}
            </div>
            {!loadingEvents && events.length === 0 && offers.length === 1 && (
              <p className="text-center text-xs text-[#0A0A0A]/50 mt-10">
                Une seule expérience à cette date — pas d&apos;événement spécial programmé.
              </p>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

/* ─── Sub-components ───────────────────────────────────────── */

function OfferCard({ o, live, dateLabel, iso }) {
  // iter-112 · Dashboard-driven price — falls back to a graceful placeholder
  // only if the /api/offers fetch failed. Gender-based offers (sunset_club)
  // display Ladies/Gents inline.
  const priceLabel = (() => {
    if (o.id === "sunset_club") {
      const pM = Number(live?.price_male ?? 25000);
      const pF = Number(live?.price_female ?? 0);
      return `${pF === 0 ? "Ladies gratuit" : `${new Intl.NumberFormat("fr-FR").format(pF)} XOF Ladies`} · ${new Intl.NumberFormat("fr-FR").format(pM)} XOF Gents`;
    }
    const pA = Number(live?.price_adult);
    if (!pA) return "Tarif sur la fiche";
    return `${new Intl.NumberFormat("fr-FR").format(pA)} XOF · / adulte`;
  })();
  return (
    <Link
      to={`/experience/${o.id}?date=${iso}`}
      onClick={() => trackEvent("bc_offer_click", { offer: o.id, date: iso })}
      className="group relative block aspect-[5/6] overflow-hidden bg-[#0A0A0A]"
      data-testid={`bc-offer-${o.id}`}
    >
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-[1.6s] ease-out group-hover:scale-[1.06]"
        style={{ backgroundImage: `url(${o.image})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/30 to-black/85" />
      <div className="absolute top-6 left-6 right-6 text-[0.55rem] tracking-[0.45em] uppercase text-[#D4B256] capitalize">
        {dateLabel}
      </div>
      <div className="absolute inset-x-0 bottom-0 p-6 md:p-7 flex items-end justify-between gap-4 text-white">
        <div className="flex-1 min-w-0">
          <h3 className="font-serif font-light text-3xl md:text-4xl leading-tight mb-2">{o.title}</h3>
          <p className="text-sm text-white/85 leading-relaxed font-light mb-3">{o.description}</p>
          <div className="text-[0.7rem] tracking-[0.18em] uppercase text-[#D4B256]" data-testid={`bc-offer-price-${o.id}`}>{priceLabel}</div>
        </div>
        <span className="flex-shrink-0 inline-flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-full border border-white/65 group-hover:bg-[#B8922A] group-hover:border-[#B8922A] transition-all duration-300">
          <ArrowRight size={18} strokeWidth={1.5} className="group-hover:translate-x-0.5 transition-transform" />
        </span>
      </div>
    </Link>
  );
}

function EventCard({ ev, iso }) {
  const title = ev.title_fr || ev.title || "Événement spécial";
  const subtitle = ev.subtitle_fr || ev.subtitle || "";
  const img = ev.image_url || HERO_IMAGE_FALLBACK;
  return (
    <Link
      to={`/event/${ev.id}?date=${iso}`}
      onClick={() => trackEvent("bc_event_click", { event: ev.id })}
      className="group relative block aspect-[5/6] overflow-hidden bg-[#0A0A0A]"
      data-testid={`bc-event-${ev.id}`}
    >
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-[1.6s] ease-out group-hover:scale-[1.06]"
        style={{ backgroundImage: `url(${img})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/30 to-black/85" />
      <div className="absolute top-6 left-6 right-6 inline-flex items-center gap-2 text-[0.55rem] tracking-[0.45em] uppercase text-[#D4B256]">
        <Sparkles size={11} strokeWidth={1.5} /> Événement spécial
      </div>
      <div className="absolute inset-x-0 bottom-0 p-6 md:p-7 flex items-end justify-between gap-4 text-white">
        <div className="flex-1 min-w-0">
          <h3 className="font-serif font-light text-3xl md:text-4xl leading-tight mb-2">{title}</h3>
          {subtitle && (
            <p className="text-sm text-white/85 leading-relaxed font-light">{subtitle}</p>
          )}
        </div>
        <span className="flex-shrink-0 inline-flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-full border border-white/65 group-hover:bg-[#B8922A] group-hover:border-[#B8922A] transition-all duration-300">
          <ArrowRight size={18} strokeWidth={1.5} className="group-hover:translate-x-0.5 transition-transform" />
        </span>
      </div>
    </Link>
  );
}
